/* Human wording for notification types.
 *
 * The registry's names (`solve.phase.advanced`) are stable identifiers the API and the
 * database key on. They are not English, and an admin deciding what their people
 * should receive should never have to read one. This module is the presentation layer
 * over them, and lives on the frontend deliberately: it is copy, and copy changing must
 * not require a backend release or a database write.
 *
 * Every seeded type is named explicitly rather than derived, because a mechanical
 * transform produces things like "Rag degraded" and "Mapitem deleted with dependents".
 * `humanise` exists only as the floor for a type a module declares after this shipped —
 * a new event stays legible instead of appearing blank.
 *
 * Titles describe WHEN the notification fires, in the second person where the recipient
 * is the subject, because that is the question an admin is answering.
 */

import { PRODUCT_NAME } from "@playbookai/types";

export interface NotificationEventLabel {
  readonly title: string;
  /** One line on when it fires. Shown under the title. */
  readonly description: string;
}

const LABELS: Readonly<Record<string, NotificationEventLabel>> = {
  invitation: {
    title: "Invitation to join the workspace",
    description: "A person is invited before they have a workspace account.",
  },
  // Identity and access
  "identity.member.added": {
    title: "You are given access to the workspace",
    description: "An admin adds you from your company directory.",
  },
  "identity.role.changed": {
    title: "Someone's role changes",
    description: "A person's permissions are changed.",
  },
  "identity.access.revoked": {
    title: "Someone's access is removed",
    description: "A person can no longer sign in.",
  },

  // Shared Teams (ADR-0082). Under identity because a team is a collection of
  // people and the registry names these types in that namespace — being on a
  // team grants nothing, so these are never about access.
  "identity.team.invited": {
    title: "You are invited to a team",
    description: "A team owner asks you to join their team.",
  },
  "identity.team.join_requested": {
    title: "Someone asks to join your team",
    description: "A request is waiting for your approval on a team you own.",
  },
  "identity.team.membership_decided": {
    title: "Your team invitation or request is answered",
    description:
      "Someone accepts or declines your invitation, or a team owner answers your request to join.",
  },

  // Administration
  "admin.export.ready": {
    title: "Your audit export is ready",
    description:
      "The Audit & compliance export you asked for can be downloaded.",
  },
  "admin.export.failed": {
    title: "Your audit export failed",
    description:
      "The Audit & compliance export you asked for could not be prepared.",
  },
  "admin.service.health_degraded": {
    title: "A service needs attention",
    description: `Something ${PRODUCT_NAME} depends on is not working properly.`,
  },
  "ai.budget.threshold_reached": {
    title: "AI spending reaches a threshold",
    description: "Assistant usage has passed a spending limit you set.",
  },
  "ai.rate_limit.threshold_reached": {
    title: "AI usage reaches its limit",
    description: "Assistant requests are being throttled.",
  },

  // Approvals, assignment, mentions
  "approval.requested": {
    title: "An approval is requested",
    description: "Someone needs a decision from you.",
  },
  "approval.decided": {
    title: "An approval is decided",
    description: "A decision you were waiting on has been made.",
  },
  "mention.created": {
    title: "Someone mentions you",
    description:
      "Someone @mentions you in a VSM discussion or a Player AI group chat.",
  },
  "comment.replied": {
    title: "Someone replies in your discussion",
    description: "Someone replies in a VSM discussion you have posted in.",
  },
  "work.item.assigned": {
    title: "Work is assigned to you",
    description: "An action or task is given to you.",
  },
  "work.item.due_soon": {
    title: "Your work is due soon",
    description: "Something assigned to you is approaching its date.",
  },
  "work.item.overdue": {
    title: "Your work is overdue",
    description: "Something assigned to you has passed its date.",
  },

  // Workspace membership and collaboration
  "workspace.member.added": {
    title: "You are added to a Solve, map, plan or campaign",
    description: "Someone gives you access, or assigns you work in it.",
  },
  "workspace.member.removed": {
    title: "You are removed from a Solve, map, plan or campaign",
    description: "The owner takes away your access.",
  },
  "workspace.member.role_changed": {
    title: "Your role on a Solve, map, plan or campaign changes",
    description:
      "The owner changes what you can do, for example from collaborator to viewer.",
  },
  "workspace.ownership.transferred": {
    title: "Ownership of a map or plan changes",
    description:
      "The owner hands a value stream map or Hoshin plan to someone else. The old and new owner are both told.",
  },
  "workspace.facilitator.granted": {
    title: "You are made facilitator",
    description: "The owner lets you run the session on a Solve, map or plan.",
  },
  "workspace.facilitator.revoked": {
    title: "You are no longer facilitator",
    description:
      "The owner takes back facilitator control, or gives it to someone else.",
  },
  "workspace.unlock.requested": {
    title: "Someone asks to edit your Solve",
    description:
      "A collaborator asks you to unlock a Solve canvas you own or facilitate.",
  },
  "workspace.canvas.unlocked": {
    title: "A canvas is unlocked",
    description:
      "The owner or facilitator unlocks a Solve, map or plan canvas, so collaborators can edit again.",
  },
  "workspace.activity.digest": {
    title: "A summary of activity",
    description: "A periodic round-up of what happened in a workspace.",
  },

  // SOLVE
  "solve.phase.advanced": {
    title: "A Solve moves forward",
    description:
      "The owner or facilitator moves a Solve you are in to its next phase.",
  },
  "solve.phase.advance_recommended": {
    title: "A Solve is ready to move on",
    description: "The next phase is suggested but not yet taken.",
  },
  // The event id stays `solve.closed` — it is the wire contract and a stored
  // preference key. Only the words move, to the ones the workspace action and
  // the notification itself now use ("Complete Solve" / "Solve completed"), so
  // a reader is not choosing a preference for an event they never see named.
  "solve.closed": {
    title: "A Solve is completed",
    description:
      "The owner or facilitator marks a Solve you are in as complete.",
  },
  "solve.ai.proposal.pending": {
    title: "AI suggests a change",
    description: "The assistant has proposed something for review.",
  },
  "solve.ai.proposal.resolved": {
    title: "An AI suggestion is answered",
    description: "A proposal has been accepted or dismissed.",
  },
  "solve.ai.proposal.stale": {
    title: "An AI suggestion is out of date",
    description: "The work moved on before a proposal was answered.",
  },
  "solve.ai.run.failed": {
    title: "An AI run fails",
    description: "The assistant could not finish what it was asked to do.",
  },

  // VSM
  "vsm.lifecycle.changed": {
    title: "A value stream map changes status",
    description: "Someone moves a map you are in between draft and active.",
  },
  "vsm.map.deleted": {
    title: "A value stream map is deleted",
    description: "The owner deletes a map you are a member of.",
  },
  "vsm.workshop.mode_changed": {
    title: "A VSM workshop starts or ends",
    description:
      "The facilitator starts the live workshop or returns the map to preparation. Player AI becomes shared with everyone, or private again.",
  },
  "vsm.share_link.enabled": {
    title: "A share link is turned on",
    description: "A map becomes reachable by link.",
  },
  "vsm.databox.completed": {
    title: "Data collection is finished",
    description: "Someone completes the figures asked of them.",
  },
  "vsm.databox.inputs_missing": {
    title: "Figures are missing",
    description: "A map cannot be calculated until data is supplied.",
  },
  "vsm.kaizen.created": {
    title: "A Kaizen is added to your map",
    description: "Someone adds a Kaizen to a map you own or facilitate.",
  },
  "vsm.kaizen.promoted": {
    title: "A Kaizen moves to the implementation plan",
    description:
      "Someone promotes a Kaizen on your map, or one you own, to the implementation plan.",
  },
  "vsm.kaizen.work_linked": {
    title: "A Kaizen is pushed to Jira",
    description:
      "Someone pushes a Kaizen on a map you own or facilitate to Jira, and the new Jira issue is linked to it.",
  },
  "vsm.kaizen.work_push_failed": {
    title: "A Kaizen push to Jira fails",
    description:
      "Someone's Jira push for a Kaizen on a map you own or facilitate fails, and no Jira issue is made.",
  },
  "vsm.kaizen.work_unlinked": {
    title: "A Jira issue is unlinked from a Kaizen",
    description:
      "Someone removes the Jira link from a Kaizen on a map you own or facilitate. The issue stays in Jira.",
  },
  "vsm.kpi.linked": {
    title: "A KPI is linked to your map",
    description: "Someone links a KPI to a map you own, or to an item on it.",
  },
  "vsm.mapitem.deleted_with_dependents": {
    title: "A map item and its links are deleted",
    description:
      "Someone deletes an item on a map you are in, and its KPI links, Kaizen links or plan rows go with it.",
  },

  // Hoshin
  "hoshin.plan.review_opened": {
    title: "A plan review opens",
    description: "A planning cycle is open for review.",
  },
  "hoshin.workshop.phase_advanced": {
    title: "A workshop moves forward",
    description:
      "The facilitator moves a Hoshin workshop you are in to its next step.",
  },
  "hoshin.target.rag_degraded": {
    title: "A target you own turns amber or red",
    description:
      "Someone moves a target you are accountable for to a worse rating.",
  },
  "hoshin.gap.opened": {
    title: "A gap opens against a target",
    description: "Performance falls short of a goal.",
  },
  "hoshin.action.at_risk": {
    title: "An action is at risk",
    description: "Planned work looks unlikely to land on time.",
  },
  "hoshin.decision.requested": {
    title: "A decision is requested",
    description: "A plan needs someone to decide.",
  },
  "hoshin.decision.overdue": {
    title: "A decision is overdue",
    description: "A decision has passed the date it was needed.",
  },

  // KPI
  "kpi.owner.assigned": {
    title: "A KPI's owner changes",
    description:
      "Someone makes you the owner of a KPI, or gives a KPI you own to someone else.",
  },
  "kpi.alert.raised": {
    title: "A measure breaches its threshold",
    description: "A KPI moves outside the range set for it.",
  },
  "kpi.alert.escalated": {
    title: "A measure alert is escalated",
    description: "An unanswered KPI alert is passed upwards.",
  },
  "kpi.alert.acknowledged": {
    title: "A measure alert is acknowledged",
    description: "Someone confirms they have seen an alert.",
  },
  "kpi.data.unavailable": {
    title: "Measure data stops arriving",
    description: "A KPI cannot be updated because its source is unavailable.",
  },
  /* The activation chase (PB-2946), seeded into the registry after this map was first
     written. Three rungs of ONE escalation, so the wording has to make the step obvious:
     an admin muting the first reminder while keeping the escalation is exactly
     the choice this page exists to offer. */
  "kpi.activation.scheduled": {
    title: "A go-live date is set for a KPI",
    description: "Someone sets the date by which a draft KPI should be active.",
  },
  "kpi.activation.overdue": {
    title: "Your KPI missed its go-live date",
    description:
      "A draft KPI you own is still not active on the date set for it.",
  },
  "kpi.activation.escalated": {
    title: "A KPI is still not live after its grace period",
    description:
      "A draft KPI is still not active 7 days after its go-live date.",
  },

  // LAMA
  "lama.reminder.pending": {
    title: "An assessment is waiting for you",
    description: "A campaign opens new questions for you to answer.",
  },
  "lama.reminder.due_soon": {
    title: "Your assessment is due soon",
    description:
      "A reminder before your assessment's due date, when the campaign has one set up.",
  },
  "lama.reminder.overdue": {
    title: "A questionnaire is overdue",
    description: "A response has passed its closing date.",
  },
  "lama.reminder.completion": {
    title: "Thank you for completing an assessment",
    description:
      "Sent after an assessment you submitted closes, when the campaign has one set up.",
  },
  "lama.results.published": {
    title: "Results are published",
    description: "Assessment results become available to view.",
  },
  "lama.wave.closed": {
    title: "A campaign wave closes",
    description:
      "Someone closes a wave of a campaign you own or analyse. Includes its response rate.",
  },
};

/**
 * Keys are the `module_id` values the registry actually stores, which are Auth's
 * module-catalogue keys — NOT the ids the producing modules call themselves.
 *
 * KPI is the one place the two vocabularies differ: it emits as `kpi` and is stored as
 * `kpi_manager`. Keying this map on `kpi` produced no crash, just a raw `kpi_manager`
 * in the Area badge, which is why `notification-event-labels.spec.ts` asserts every
 * stored id has a label rather than leaving it to be noticed on screen.
 */
const MODULE_NAMES: Readonly<Record<string, string>> = {
  platform: "Platform",
  solve: "SOLVE",
  vsm: "VSM",
  hoshin: "Hoshin",
  kpi_manager: "KPI Manager",
  lama: "LAMA",
};

/**
 * Readable wording for a type nobody has named yet.
 *
 * Drops the leading namespace and turns the rest into a sentence, so a module that
 * declares `solve.retro.scheduled` after this shipped reads as "Retro scheduled" rather
 * than as a raw key. A floor, not a substitute for naming it properly.
 */
export function humanise(typeName: string): string {
  const parts = typeName.split(".").slice(1);
  const words = (parts.length > 0 ? parts : [typeName])
    .join(" ")
    .replace(/_/g, " ")
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Own-property lookup, because the key comes from the server registry.
 *
 * These maps are plain object literals, so `Object.prototype` keys are reachable through
 * index access. A type or module named `constructor`, `toString` or `valueOf` would
 * return an inherited FUNCTION — truthy, so `??` never reaches the fallback, and the row
 * would render a blank title or React would print function source inside the Badge.
 * Neither is a crash, which is what makes it worth guarding: it would simply look wrong.
 */
function lookup<T>(
  table: Readonly<Record<string, T>>,
  key: string,
): T | undefined {
  return Object.hasOwn(table, key) ? table[key] : undefined;
}

export function labelForNotificationType(
  typeName: string,
): NotificationEventLabel {
  return (
    lookup(LABELS, typeName) ?? {
      title: humanise(typeName),
      description: "",
    }
  );
}

/** Which part of the product an event comes from. Null means every module. */
export function labelForModule(moduleId: string | null): string {
  if (moduleId === null) return "All modules";
  return lookup(MODULE_NAMES, moduleId) ?? moduleId;
}
